import React, { useEffect, useState } from 'react'
import {
  TextField, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, IconButton, Chip, CircularProgress
} from '@mui/material'
import { DeleteOutline } from '@mui/icons-material'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import AlertUI from '../../generic/AlertUI'

// D12 (2026-07-15, second PO amendment) — replaces D11's 3-option selector. The modal is now a
// table with the FULL catalogue of technologies and groups (not just the worker's existing
// links): one row per skill, one checkbox column per phase (Prepare/Execute today — adding a
// future phase is just another column, D1's reason to be for two independent booleans instead of
// a combined enum). Any phase checked = the worker has that skill; all phases unchecked = the
// worker does not have it (removed/never created on save) — RN2's "no capability" state is no
// longer representable as an invalid link, it simply means "not linked". Uses the house
// SmartDialog generic (like BalancedColumnReorderDialog.jsx, PartPreset.jsx, etc.) instead of a
// raw MUI Dialog, for consistency with the rest of the app.
//
// Historia 121 (D11 of that story): a TECHNOLOGY row also carries the machine scope of the skill —
// "All machines" (default, RN3) or "Only some (n)" (RN4) — with a machine selector (one prepare and
// one execute checkbox per machine) opened lazily from the row (CU2). While the scope is limited the
// row's own phase checkboxes are the DERIVED union of its machines and are disabled (RN7). Every row
// offers an explicit "Remove skill" action (RN38 / D-G); on "All machines" rows unchecking every
// phase still removes the skill (the historical gesture). Group rows have no scope (RN8).
const PHASES = [
  { flag: 'canPrepare', labelKey: 'Prepare' },
  { flag: 'canExecute', labelKey: 'Execute' }
]

export const ALL_DEVICES = 'ALL_DEVICES'
export const SELECTED_DEVICES = 'SELECTED_DEVICES'

const anyPhase = (holder) => PHASES.some((phase) => !!holder[phase.flag])

// Whether a row is a skill the worker has: a limited technology row is linked while at least one
// machine has a phase (RN6); any other row while it has a phase checked (D12).
export const isLinked = (row) => (
  !row.isGroup && row.deviceScope === SELECTED_DEVICES
    ? row.scopedDevices.some(anyPhase)
    : anyPhase(row)
)

// RN7 — the link-level flags of a limited row are the OR of its machines, never edited directly.
export const derivedFlag = (row, flag) => row.scopedDevices.some((device) => !!device[flag])

const displayedFlag = (row, flag) => (
  !row.isGroup && row.deviceScope === SELECTED_DEVICES ? derivedFlag(row, flag) : !!row[flag]
)

const toScopedDevice = (device) => ({
  deviceId: device.deviceId,
  deviceName: device.deviceName || '',
  canPrepare: !!device.canPrepare,
  canExecute: !!device.canExecute
})

// Builds one row per catalogue entry (technology or group), merging in the worker's persisted
// flags where a link exists. Unlinked rows default to every phase unchecked (D12) — the RN3/V3
// "both active" default only applies to links created via the WorkerSettings multiselects, not to
// rows checked directly in this table (D12/S9: checking a single phase here is an explicit
// gesture that must not auto-complete the other phase). Rows are always sorted alphabetically by
// name, independent of link status or what the user checks/unchecks (D12 amendment 2026-07-15):
// the table must never reorder itself while editing.
export const buildRows = (worker, technologies, technologyGroups) => {
  const links = worker.workerTechnologies || []
  const findLink = (id, isGroup) => links.find((link) => link.isGroup === isGroup && link.id === id)

  const technologyRows = (technologies || []).map((technology) => {
    const link = findLink(technology.id, false)
    return {
      id: technology.id,
      name: technology.name,
      isGroup: false,
      fakeId: `technology_${technology.id}`,
      canPrepare: link ? !!link.canPrepare : false,
      canExecute: link ? !!link.canExecute : false,
      groupTechnologies: [],
      // Historia 121 (RN29): an absent scope on a persisted link reads as "all machines" (RN3).
      deviceScope: link && link.deviceScope === SELECTED_DEVICES ? SELECTED_DEVICES : ALL_DEVICES,
      scopedDevices: link && Array.isArray(link.scopedDevices) ? link.scopedDevices.map(toScopedDevice) : []
    }
  })

  const groupRows = (technologyGroups || []).map((group) => {
    const link = findLink(group.id, true)
    return {
      id: group.id,
      name: group.name,
      isGroup: true,
      fakeId: `group_${group.id}`,
      canPrepare: link ? !!link.canPrepare : false,
      canExecute: link ? !!link.canExecute : false,
      groupTechnologies: group.technologies || [],
      deviceScope: ALL_DEVICES,
      scopedDevices: []
    }
  })

  return [...technologyRows, ...groupRows].sort((a, b) => a.name.localeCompare(b.name))
}

// The payload item of a linked row (§ 8.1). Only technology rows carry the scope; a limited scope
// sends its machines with their own flags (the link flags travel derived, not as source of truth).
export const toPayloadLink = (row) => {
  const link = {
    id: row.id,
    name: row.name,
    isGroup: row.isGroup,
    fakeId: row.fakeId,
    canPrepare: displayedFlag(row, 'canPrepare'),
    canExecute: displayedFlag(row, 'canExecute')
  }
  if (row.isGroup) {
    return link
  }
  if (row.deviceScope === SELECTED_DEVICES) {
    return {
      ...link,
      deviceScope: SELECTED_DEVICES,
      scopedDevices: row.scopedDevices.filter(anyPhase).map((device) => ({
        deviceId: device.deviceId,
        canPrepare: !!device.canPrepare,
        canExecute: !!device.canExecute
      }))
    }
  }
  return { ...link, deviceScope: ALL_DEVICES }
}

const SkillCapabilitiesDialog = ({ open, workerId, onClose, onSaved }) => {
  const { t_ } = useSmartTranslation()
  const [worker, setWorker] = useState({})
  const [rows, setRows] = useState([])
  const [searchText, setSearchText] = useState('')
  // Historia 121 (CU2): machine catalogue per technology id, fetched lazily the first time a row
  // goes "Only some" — never when the modal opens (the modal already makes 3 calls on open).
  const [machinesByTechnology, setMachinesByTechnology] = useState({})
  const [loadingTechnologies, setLoadingTechnologies] = useState([])
  // Technologies whose catalogue request failed: not retried in a loop by the effect below; the
  // user closes and reopens the modal to retry.
  const [failedTechnologies, setFailedTechnologies] = useState([])
  const [AlertElement, showAlert, dismissAlert] = AlertUI()

  // Error alerts never auto-dismiss (AlertUI sets no duration for severity 'error') and this
  // component stays mounted after the dialog closes, so the alert must be dismissed explicitly
  // on every exit path or it would linger on screen with the dialog gone.
  const closeDialog = () => {
    dismissAlert()
    onClose()
  }

  // Loads a fresh worker AND the full catalogue on every open, independent of the shape of the
  // caller's row/list data (D11, kept by D12): both entry points (Workers.jsx row menu and
  // WorkerSettings header menu) can open this dialog without needing the full worker DTO or the
  // catalogue already in hand.
  useEffect(() => {
    if (!open) return
    const loadData = async () => {
      const [workerDB, technologiesDB, technologyGroupsDB] = await Promise.all([
        apiGet('worker/' + workerId),
        apiGet('technologiesBasicData'),
        apiGet('technologyGroupsNoPagination')
      ])
      setWorker(workerDB)
      setRows(buildRows(workerDB, technologiesDB, technologyGroupsDB))
      setMachinesByTechnology({})
      setLoadingTechnologies([])
    }
    loadData()
  }, [open, workerId])

  const updateRow = (fakeId, update) => {
    setRows((currentRows) => currentRows.map((row) => (row.fakeId === fakeId ? update(row) : row)))
  }

  const onTogglePhase = (fakeId, flag, checked) => {
    updateRow(fakeId, (row) => ({ ...row, [flag]: checked }))
  }

  // Historia 121, CU2 — the machines of a technology, fetched once per open and pruned into the
  // row: a persisted machine the catalogue no longer returns lost the technology and is dropped
  // from what will be sent (S51: keeping it would be E5 on the server).
  const loadMachines = async (row) => {
    if (machinesByTechnology[row.id] || loadingTechnologies.includes(row.id) || failedTechnologies.includes(row.id)) return
    setLoadingTechnologies((current) => [...current, row.id])
    let catalogue
    try {
      catalogue = await apiGet(`technology/${row.id}/devicesBasicData`)
    } catch (error) {
      catalogue = undefined
    }
    setLoadingTechnologies((current) => current.filter((id) => id !== row.id))
    // A rejected request, or the `undefined` the API layer resolves on a lost connection, is NOT an
    // empty catalogue: the persisted machines are kept untouched (no pruning, S51 applies only to a
    // real catalogue) and the user is told in place (audit 2026-09-10).
    if (!Array.isArray(catalogue)) {
      setFailedTechnologies((current) => (current.includes(row.id) ? current : [...current, row.id]))
      showAlert({ severity: 'error', title: t_('¡Ups, something has gone wrong!') })
      return
    }
    const machines = catalogue
    setMachinesByTechnology((current) => ({ ...current, [row.id]: machines }))
    const knownIds = new Set(machines.map((machine) => machine.id))
    updateRow(row.fakeId, (current) => ({
      ...current,
      scopedDevices: current.scopedDevices
        .filter((device) => knownIds.has(device.deviceId))
        .map((device) => ({
          ...device,
          deviceName: (machines.find((machine) => machine.id === device.deviceId) || {}).name || device.deviceName
        }))
    }))
  }

  // Historia 121, CU2 — the catalogue of a technology is fetched the moment one of its rows is
  // limited (persisted as such, or just switched to "Only some"), never on open.
  useEffect(() => {
    rows
      .filter((row) => !row.isGroup && row.deviceScope === SELECTED_DEVICES &&
        !machinesByTechnology[row.id] && !loadingTechnologies.includes(row.id) && !failedTechnologies.includes(row.id))
      .forEach((row) => { loadMachines(row) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, machinesByTechnology, loadingTechnologies, failedTechnologies])

  // Historia 121 — scope control of a technology row. Going limited opens the selector (lazy
  // catalogue, see the effect above); going back to "All machines" discards the selection and
  // hands the (derived) flags back to the row as editable values (RN30 / S52).
  const onChangeScope = (row, deviceScope) => {
    if (deviceScope === SELECTED_DEVICES) {
      updateRow(row.fakeId, (current) => ({ ...current, deviceScope: SELECTED_DEVICES }))
      return
    }
    updateRow(row.fakeId, (current) => ({
      ...current,
      deviceScope: ALL_DEVICES,
      canPrepare: current.scopedDevices.length > 0 ? derivedFlag(current, 'canPrepare') : current.canPrepare,
      canExecute: current.scopedDevices.length > 0 ? derivedFlag(current, 'canExecute') : current.canExecute,
      scopedDevices: []
    }))
  }

  // Historia 121 — one machine's phase checkbox. A machine with no phase left is simply not
  // selected (RN5): it leaves the scope, and if it was inactive it can no longer be re-added (RN39).
  const onToggleMachinePhase = (row, machine, flag, checked) => {
    updateRow(row.fakeId, (current) => {
      const existing = current.scopedDevices.find((device) => device.deviceId === machine.id)
      const updated = existing
        ? { ...existing, [flag]: checked }
        : { deviceId: machine.id, deviceName: machine.name, canPrepare: false, canExecute: false, [flag]: checked }
      const others = current.scopedDevices.filter((device) => device.deviceId !== machine.id)
      return { ...current, scopedDevices: anyPhase(updated) ? [...others, updated] : others }
    })
  }

  // Historia 121, RN38 / D-G — explicit removal of a skill, whatever its scope: the row goes back
  // to "not linked", so the link does not travel in the complete desired list (RN29).
  const onRemoveSkill = (fakeId) => {
    updateRow(fakeId, (row) => ({
      ...row, canPrepare: false, canExecute: false, deviceScope: ALL_DEVICES, scopedDevices: []
    }))
  }

  const filteredRows = rows.filter((row) => row.name.toLowerCase().includes(searchText.toLowerCase()))

  // Mínimo una skill (D12/S10): a worker must keep at least one row with some phase checked —
  // mirrors the mandatory "Skills" column in the Workers list. Computed over every row, not just
  // the filtered/visible ones, since the search box only narrows the view.
  const hasAnySkill = rows.some(isLinked)

  const handleSave = () => {
    // Historia 121, V2/E3 mirror (S49): a limited skill without any machine with a phase is not a
    // removal — it is invalid. Emptying the selector is NOT how a skill is removed (RN38). Checked
    // BEFORE the "at least one skill" rule: when the emptied row is the worker's only skill the
    // user must be told to pick a machine, not that the worker has no skill (audit 2026-09-10).
    const limitedWithoutMachines = rows.some((row) =>
      !row.isGroup && row.deviceScope === SELECTED_DEVICES && !row.scopedDevices.some(anyPhase))
    if (limitedWithoutMachines) {
      showAlert({ severity: 'error', title: t_('Select at least one machine with at least one phase') })
      return
    }
    // Mínimo una skill (D12/S10, PO correction 2026-07-15): the Save action is always
    // visible/enabled (no hidden/disabled button — house pattern); accepting with every row
    // unchecked shows an error alert instead, and neither saves nor closes the dialog.
    if (!hasAnySkill) {
      showAlert({ severity: 'error', title: t_('The worker must have at least one skill.') })
      return
    }

    const workerTechnologies = rows.filter(isLinked).map(toPayloadLink)

    apiPut('worker', { ...worker, workerTechnologies })
      .then(() => {
        if (onSaved) onSaved()
        closeDialog()
      })
      .catch((error) => {
        // Historia 121 (S53): the server's rejection (E2 logged-in worker, E3-E9 scope errors) is
        // shown in place; the modal stays open with the user's edits intact.
        showAlert({ severity: 'error', title: (error && error.message) || t_('¡Ups, something has gone wrong!') })
      })
  }

  // Historia 121, RN39 / D-F — the machines offered for a limited row: the ACTIVE ones plus the
  // inactive ones already in the scope (marked inactive; they can be removed, never added).
  const machinesToOffer = (row) => {
    const catalogue = machinesByTechnology[row.id] || []
    const selectedIds = new Set(row.scopedDevices.map((device) => device.deviceId))
    return catalogue.filter((machine) => machine.active !== false || selectedIds.has(machine.id))
  }

  const scopeLabel = (row) => (
    row.deviceScope === SELECTED_DEVICES
      ? `${t_('Only some')} (${row.scopedDevices.filter(anyPhase).length})`
      : t_('All machines')
  )

  const renderMachineSelector = (row) => (
    <TableRow key={`${row.fakeId}-machines`} data-testid={`machine-selector-${row.fakeId}`}>
      <TableCell colSpan={2 + PHASES.length} sx={{ paddingLeft: '2rem' }}>
        <div style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>{t_('Select machines')}</div>
        {loadingTechnologies.includes(row.id) && <CircularProgress size={18} />}
        {failedTechnologies.includes(row.id) && (
          <div data-testid={`machines-error-${row.fakeId}`} style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>
            {t_('¡Ups, something has gone wrong!')}
          </div>
        )}
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>{t_('Machine')}</TableCell>
              {PHASES.map((phase) => (
                <TableCell key={phase.flag} align='center'>{t_(phase.labelKey)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {machinesToOffer(row).map((machine) => {
              const selected = row.scopedDevices.find((device) => device.deviceId === machine.id)
              const inactive = machine.active === false
              return (
                <TableRow key={machine.id} data-testid={`machine-row-${row.fakeId}-${machine.id}`}>
                  <TableCell>
                    {machine.name}
                    {inactive && (
                      <Chip size='small' label={t_('Inactive')} variant='outlined' sx={{ marginLeft: '0.5rem' }} />
                    )}
                  </TableCell>
                  {PHASES.map((phase) => (
                    <TableCell key={phase.flag} align='center'>
                      <Checkbox
                        checked={!!(selected && selected[phase.flag])}
                        // An inactive machine can only be kept or removed, never (re)added (RN39).
                        disabled={inactive && !selected}
                        onChange={(event) => onToggleMachinePhase(row, machine, phase.flag, event.target.checked)}
                        inputProps={{ 'aria-label': `${t_(phase.labelKey)} ${machine.name}` }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableCell>
    </TableRow>
  )

  const renderRow = (row) => {
    const limited = !row.isGroup && row.deviceScope === SELECTED_DEVICES
    return (
      <React.Fragment key={row.fakeId}>
        <TableRow data-testid={`skill-row-${row.fakeId}`}>
          <TableCell>
            {row.name}
            {row.isGroup && (
              <div data-testid={`group-technologies-${row.fakeId}`}>
                {row.groupTechnologies.map((tech) => tech.name).join(', ')}
              </div>
            )}
          </TableCell>
          <TableCell>
            {!row.isGroup && (
              <TextField
                select
                size='small'
                value={row.deviceScope}
                onChange={(event) => onChangeScope(row, event.target.value)}
                SelectProps={{ native: true }}
                inputProps={{ 'aria-label': t_('Scope'), 'data-testid': `scope-select-${row.fakeId}` }}
              >
                <option value={ALL_DEVICES}>{t_('All machines')}</option>
                <option value={SELECTED_DEVICES}>{scopeLabel({ ...row, deviceScope: SELECTED_DEVICES })}</option>
              </TextField>
            )}
          </TableCell>
          {PHASES.map((phase) => (
            <TableCell key={phase.flag} align='center'>
              <Checkbox
                checked={displayedFlag(row, phase.flag)}
                disabled={limited}
                onChange={(event) => onTogglePhase(row.fakeId, phase.flag, event.target.checked)}
                inputProps={{ 'aria-label': t_(phase.labelKey) }}
              />
            </TableCell>
          ))}
          <TableCell align='center'>
            <IconButton
              size='small'
              aria-label={t_('Remove skill')}
              data-testid={`remove-skill-${row.fakeId}`}
              disabled={!isLinked(row)}
              onClick={() => onRemoveSkill(row.fakeId)}
            >
              <DeleteOutline fontSize='small' />
            </IconButton>
          </TableCell>
        </TableRow>
        {limited && renderMachineSelector(row)}
      </React.Fragment>
    )
  }

  return (
    <>
      {/* Rendered above the MUI Dialog's own stacking context (theme.zIndex.modal, 1300 by
          default) so the "at least one skill" error alert stays visible while the modal is open
          (PO correction 2026-07-15). */}
      {open && (
        <div style={{ position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)', zIndex: 1301, width: '90%', maxWidth: '40rem' }}>
          {AlertElement}
        </div>
      )}
      <SmartDialog
        setOpen={open}
        title={createDialogTitle({ color: 'primary', title: t_('Skills') })}
        renderComponent={
          <div style={{ width: '100%', textAlign: 'left' }}>
            <TextField
              fullWidth
              placeholder={t_('Search...')}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              sx={{ marginBottom: '1rem' }}
            />
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t_('Machines')}</TableCell>
                  {PHASES.map((phase) => (
                    <TableCell key={phase.flag} align='center'>{t_(phase.labelKey)}</TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map(renderRow)}
              </TableBody>
            </Table>
          </div>
        }
        acceptLabel='Save'
        acceptCallback={handleSave}
        cancel
        cancelLabel='Cancel'
        cancelCallback={closeDialog}
        minWidth='48rem'
      />
    </>
  )
}

export default SkillCapabilitiesDialog
